import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  PortfolioBuildingRowDto,
  PortfolioListDto,
  SensorStatisticService,
  SortDto,
} from '@artemis-software/wr-api';
import { Observable, firstValueFrom, from } from 'rxjs';
import { isInternalTenant } from '@/utils/admin-utils';

interface PortfolioFilter {
  text?: string;
  ownerTenantId?: string | null;
  page?: number;
  size?: number;
  sort?: SortDto;
}

interface OwnerTenantOption {
  id: string;
  name: string;
}

type SortableKey = keyof Pick<
  PortfolioBuildingRowDto,
  'name' | 'address' | 'internalNumber' | 'livingArea' | 'heatPerArea' | 'coldWaterPerArea' | 'electricityPerArea' | 'solarSavingsPerArea'
>;

@Component({
  standalone: false,
  selector: 'wr-portfolio-overview-page',
  templateUrl: './portfolio-overview-page.component.html',
  styleUrls: ['./portfolio-overview-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioOverviewPageComponent {
  private readonly sensorStatisticService = inject(SensorStatisticService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);

  readonly ownerTenantOptions = signal<OwnerTenantOption[]>([]);
  readonly isInternalTenant = isInternalTenant();

  readonly defaultSort: SortDto = { sort: 'heatPerArea', order: SortDto.OrderEnum.Desc };

  private listPromise?: Promise<PortfolioListDto>;

  readonly form: FormGroup = this.formBuilder.group({
    text: this.formBuilder.control(''),
    ownerTenantId: this.formBuilder.control<string | null>(null),
  });

  readonly loadRows = (filter: PortfolioFilter): Observable<PortfolioBuildingRowDto[]> =>
    from(
      this.resolveRows(filter).then((rows) => {
        const page = filter.page ?? 0;
        const size = filter.size ?? 10;
        return rows.slice(page * size, page * size + size);
      }),
    );

  readonly getCount = (filter: PortfolioFilter): Observable<number> =>
    from(this.resolveRows(filter).then((rows) => rows.length));

  navigateTo(row: PortfolioBuildingRowDto): void {
    this.router.navigate(['building', row.buildingId]);
  }

  private loadList(): Promise<PortfolioListDto> {
    if (!this.listPromise) {
      this.listPromise = firstValueFrom(this.sensorStatisticService.findPortfolioList())
        .then((list) => {
          const options = this.buildOwnerTenantOptions(list.rows);
          this.ownerTenantOptions.set(options);
          const selectedOwnerId = this.form.value.ownerTenantId;
          if (selectedOwnerId && !options.some((option) => option.id === selectedOwnerId)) {
            this.form.patchValue({ ownerTenantId: null });
          }
          return list;
        })
        .catch((error) => {
          this.listPromise = undefined;
          throw error;
        });
    }
    return this.listPromise;
  }

  private async resolveRows(filter: PortfolioFilter): Promise<PortfolioBuildingRowDto[]> {
    const list = await this.loadList();
    return this.sortRows(this.filterRows(list.rows, filter), filter.sort);
  }

  private buildOwnerTenantOptions(rows: PortfolioBuildingRowDto[]): OwnerTenantOption[] {
    const byId = new Map<string, string>();
    for (const row of rows) {
      byId.set(row.ownerTenantId, row.ownerTenantName);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }

  private filterRows(rows: PortfolioBuildingRowDto[], filter: PortfolioFilter): PortfolioBuildingRowDto[] {
    const ownerFiltered = filter.ownerTenantId
      ? rows.filter((row) => row.ownerTenantId === filter.ownerTenantId)
      : rows;
    const needle = filter.text?.trim().toLowerCase();
    if (!needle) return ownerFiltered;
    return ownerFiltered.filter((row) =>
      [row.name, row.address, row.internalNumber].some((value) => value?.toLowerCase().includes(needle)),
    );
  }

  private sortRows(rows: PortfolioBuildingRowDto[], sort?: SortDto): PortfolioBuildingRowDto[] {
    if (!sort?.sort) return rows;
    const key = sort.sort as SortableKey;
    const direction = sort.order === SortDto.OrderEnum.Desc ? -1 : 1;
    return [...rows].sort((a, b) => {
      const left = a[key];
      const right = b[key];
      // Objekte ohne Kennzahl bleiben am Ende, unabhängig von der Sortierrichtung.
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * direction;
      }
      return String(left).localeCompare(String(right), 'de', { numeric: true }) * direction;
    });
  }
}
